const FALLBACK_ZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Asia/Tokyo",
];

export function userTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function readParts(date, timeZone, options) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, ...options }).formatToParts(date);
  const map = {};
  parts.forEach((part) => {
    if (part.type !== "literal") map[part.type] = part.value;
  });
  return map;
}

function offsetMs(instant, timeZone) {
  const map = readParts(instant, timeZone, {
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  let year = Number(map.year);
  let month = Number(map.month);
  let day = Number(map.day);
  let hour = Number(map.hour);
  if (hour === 24) {
    hour = 0;
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    year = next.getUTCFullYear();
    month = next.getUTCMonth() + 1;
    day = next.getUTCDate();
  }
  const asUtc = Date.UTC(year, month - 1, day, hour, Number(map.minute), Number(map.second));
  return asUtc - instant.getTime();
}

export function zonedDateTime(year, monthIndex, day, hour, minute, timeZone) {
  const wallAsUtc = Date.UTC(year, monthIndex, day, hour, minute, 0);
  let utc = wallAsUtc;
  for (let pass = 0; pass < 2; pass += 1) {
    utc = wallAsUtc - offsetMs(new Date(utc), timeZone);
  }
  return new Date(utc);
}

export function civilParts(date, timeZone) {
  const map = readParts(date, timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
  };
}

export function civilKey(parts) {
  return parts.year * 10000 + parts.month * 100 + parts.day;
}

export function calendarCivil(date) {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

export function addCivilDays(parts, days) {
  const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  };
}

export function monthOfZoneToday(now, timeZone) {
  const civil = civilParts(now, timeZone);
  return new Date(civil.year, civil.month - 1, 1);
}

export function timeZoneAbbreviation(date, timeZone) {
  try {
    const map = readParts(date, timeZone, { timeZoneName: "short" });
    return map.timeZoneName || "";
  } catch {
    return "";
  }
}

export function timeZoneCaption(date = new Date(), timeZone = userTimeZone()) {
  const abbreviation = timeZoneAbbreviation(date, timeZone);
  if (abbreviation && abbreviation !== timeZone) {
    return `Times shown in ${timeZone} (${abbreviation})`;
  }
  return `Times shown in ${timeZone}`;
}

export function timeZoneOptionLabel(zone, date) {
  const abbreviation = timeZoneAbbreviation(date, zone);
  const name = zone.replace(/_/g, " ");
  if (abbreviation && abbreviation !== zone) return `${name} (${abbreviation})`;
  return name;
}

export function listTimeZones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch {
    return FALLBACK_ZONES;
  }
  return FALLBACK_ZONES;
}

export function timeZoneOptions(date = new Date(), selected) {
  const zones = listTimeZones();
  const values = selected && !zones.includes(selected) ? [selected, ...zones] : zones;
  return values.map((zone) => ({
    value: zone,
    label: timeZoneOptionLabel(zone, date),
  }));
}

export function formatSlotTime(date, timeZone = userTimeZone()) {
  const map = readParts(date, timeZone, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${map.hour}:${map.minute} ${map.dayPeriod}`;
}

export function formatConfirmation(date, timeZone = userTimeZone()) {
  const map = readParts(date, timeZone, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return `${map.weekday}, ${map.month} ${map.day}, ${map.year} at ${map.hour}:${map.minute} ${map.dayPeriod}`;
}

export function formatPreferTimeSlot(date, timeZone = userTimeZone()) {
  const map = readParts(date, timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h12",
  });
  const month = String(map.month).padStart(2, "0");
  const day = String(map.day).padStart(2, "0");
  const hour = String(map.hour).padStart(2, "0");
  const minute = String(map.minute).padStart(2, "0");
  const period = String(map.dayPeriod || "").toUpperCase();
  return `${month}-${day}-${map.year} ${hour}:${minute} ${period}`;
}

export function slotKey(date, timeZone = userTimeZone()) {
  const map = readParts(date, timeZone, {
    hourCycle: "h23",
    hour: "2-digit",
    minute: "2-digit",
  });
  const hour = map.hour === "24" ? "00" : map.hour;
  return `${hour}-${map.minute}`;
}
